import { ChangeEvent, FC, Suspense, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import SaveIcon from "@mui/icons-material/Save";
import StackedLineChartIcon from "@mui/icons-material/StackedLineChart";
import {
	Box,
	ButtonGroup,
	Chip,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { green, grey } from "@mui/material/colors";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { Translate } from "@/components/Application/Translate";
import { MeasurementsHomePage } from "@/components/Measurements/Pages/MeasurementsHomePage";
import { ManualMeasurementPointSummary } from "@/components/Measurements/Types/MeasurementGroups";
import { useFetchChecklistStepStatuses } from "@/components/Procedures/API/ChecklistQueries";

import { ErrorToast } from "@/helpers/alerts/AlertToast";
import StyledFullModal from "@/helpers/components/StyledFullModal";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { UpdateStepStatusRequest } from "@/model/Procedure/UpdateStepStatusRequest";

import { useUpdateStepStatus } from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";

dayjs.extend(utc);

type Props = {
	step: ProcedureNodeStep;
	activeSchemeId: number | undefined;
	isDisabled?: boolean;
};

export const StepManualMeasurements: FC<Props> = ({
	step,
	activeSchemeId,
	isDisabled,
}) => {
	let { id } = useParams<RouteParams>();
	const procedureId = parseInt(id);
	const [saveStateSuccess, setSaveStateSuccess] = useState<boolean>(true);

	// keep what the user typed
	const [inputValue, setInputValue] = useState<string>("");

	// keep the parsed number that will be sent to the API
	const [manualRegistration, setManualRegistration] = useState<
		number | undefined
	>(undefined);

	const [measurementModalOpen, toggleMeasurementModalOpen] =
		useState<boolean>(false);

	const { data: stepStatuses } = useFetchChecklistStepStatuses(
		activeSchemeId,
		procedureId,
	);
	const currentStepStatus = Array.isArray(stepStatuses)
		? stepStatuses.find((x) => x.Id === step.Id)
		: undefined;

	const updateStepStatus = useUpdateStepStatus();

	// initialize from current step status
	useEffect(() => {
		if (!currentStepStatus?.Status) {
			setSaveStateSuccess(false);
			setInputValue(
				currentStepStatus?.ManualMeasurementValue?.toString() ?? "",
			);
		} else if (currentStepStatus?.ManualMeasurementValue !== undefined) {
			const val = +currentStepStatus.ManualMeasurementValue;
			setManualRegistration(val);
			// keep the user's view exactly as a string (using . here, but you can format differently)
			setInputValue(currentStepStatus.ManualMeasurementValue.toString());
			setSaveStateSuccess(true);
		}
	}, [currentStepStatus?.Status, currentStepStatus?.ManualMeasurementValue]);

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setInputValue(value); // always reflect what user types

		if (!value) {
			setManualRegistration(undefined);
			return;
		}

		// allow numbers with , or .
		const regex = /^-?\d*(?:[.,]\d*)?$/;
		if (regex.test(value)) {
			// only parse when it's not just "-" or ends with "," or "."
			const endsWithSeparator = value.endsWith(",") || value.endsWith(".");
			const isOnlyMinus = value === "-";

			if (!endsWithSeparator && !isOnlyMinus) {
				const normalized = value.replace(",", ".");
				const num = Number(normalized);
				if (!Number.isNaN(num)) {
					setManualRegistration(num);
				} else {
					setManualRegistration(undefined);
				}
			}
		} else {
			// invalid char – show error but don't change the parsed number
			ErrorToast(
				<Translate String="Procedure.Checklist.ManualReading.ValueField.Error">
					Please enter a valid number.
				</Translate>,
			);
		}
	};

	const Save = async () => {
		try {
			const request: UpdateStepStatusRequest = {
				ArchiveType: "Measurement",
				MeasurementId: step.ManualReading?.Id,
				ProcedureId: procedureId,
				SchemeId: activeSchemeId,
				Status: 1,
				StepId: step.Id,
				Value: manualRegistration,
			};
			await updateStepStatus(request);

			setSaveStateSuccess(true);
		} catch (error: any) {
			setSaveStateSuccess(false);
			ErrorToast(error.message);
		}
	};

	if (!step?.ManualReading) {
		return <></>;
	}

	const initialManualReading: ManualMeasurementPointSummary = {
		Id: step.ManualReading.Id,
		Name: step.ManualReading.Name,
	};

	return (
		<Box sx={{ display: "flex" }}>
			<Chip
				sx={{
					height: "120px",
					backgroundColor: saveStateSuccess ? green[300] : grey[200],
					boxShadow: 2,
				}}
				label={
					<Box display="flex" flexDirection="column" gap={0.25} p={2}>
						<Typography color={grey[900]} sx={{ mb: 1.5 }}>
							{step.ManualReading?.Name}
						</Typography>
						<TextField
							sx={{
								alignSelf: "center",
								width: "100%",
								borderWidth: "0px",
								"& .MuiOutlinedInput-root": {
									backgroundColor: saveStateSuccess ? green[200] : "primary",
									color: grey[800],
									"&.Mui-focused fieldset": {
										borderColor: grey[800],
										borderWidth: "1px",
									},
								},
							}}
							size="small"
							focused
							disabled={isDisabled}
							name={`${step.ManualReading.Id}`}
							value={inputValue}
							inputProps={{
								inputMode: "decimal",
								pattern: "[0-9]*[.,]?[0-9]*",
							}}
							onKeyDown={(e) => {
								// allow control keys + , .
								if (
									[
										"Backspace",
										"Delete",
										"Tab",
										"Escape",
										"Enter",
										"ArrowLeft",
										"ArrowRight",
										".",
										",",
										"-",
									].includes(e.key)
								) {
									return;
								}
								if (!/^[0-9]$/.test(e.key)) {
									e.preventDefault();
								}
							}}
							label={
								<Translate String="Procedure.Checklist.ManualReading.ValueField">
									Value
								</Translate>
							}
							onChange={onChange}
						/>
						<Typography variant="caption" color={grey[900]} sx={{ gap: 0 }}>
							<Translate String="Procedure.Checklist.ManualReading.ValueRange">
								Range: {step.ManualReading.Min} - {step.ManualReading.Max}
							</Translate>
						</Typography>
					</Box>
				}
			/>
			<ButtonGroup
				orientation="vertical"
				sx={{
					ml: 1,
				}}
			>
				<Tooltip
					title={
						<Translate String="Checklist.Measurement.Page.Button.ToolTip">
							Measurement page
						</Translate>
					}
					placement="top"
				>
					<IconButton
						color="info"
						onClick={() => toggleMeasurementModalOpen(true)}
					>
						<StackedLineChartIcon />
					</IconButton>
				</Tooltip>
				<Tooltip
					title={
						<Translate String="Checklist.Measurement.Save.Button.ToolTip">
							Save
						</Translate>
					}
				>
					<IconButton
						color="success"
						onClick={Save}
						disabled={saveStateSuccess}
					>
						<SaveIcon />
					</IconButton>
				</Tooltip>
			</ButtonGroup>
			<StyledFullModal
				open={measurementModalOpen}
				onClose={() => toggleMeasurementModalOpen(false)}
				title={step.ManualReading?.Name}
				isFullScreen={false}
			>
				<Suspense fallback={null}>
					<MeasurementsHomePage
						initialSelectedMeasurement={initialManualReading}
					/>
				</Suspense>
			</StyledFullModal>
		</Box>
	);
};
